import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Container } from "@/components/layout/Container";
import { PageHeader } from "@/components/content/PageHeader";
import { EmptyState } from "@/components/content/EmptyState";
import { getSubjectIcon } from "@/components/content/SubjectIcon";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useClasses, useSubjects } from "@/hooks/useCurriculum";
import { StaggerList, StaggerItem } from "@/components/motion/Stagger";
import { cn } from "@/lib/cn";

const CLASS_OPTIONS = ["class-8", "class-9", "class-10", "class-11", "class-12"];

function classLabel(id: string) {
  return id.replace("class-", "Class ");
}

export function SubjectsPage() {
  const [params, setParams] = useSearchParams();
  const { status } = useAuth();
  const { data: me } = useCurrentUser();
  const { data: classes } = useClasses();

  // Class precedence: ?class= query > user.class_id > class-11 (default for anon).
  const fallbackClass = me?.class_id || "class-11";
  const classId = params.get("class") || fallbackClass;

  const { data: subjects, isLoading, isError } = useSubjects(classId);

  // Compose the class picker list - merge known options with whatever the server returned.
  const classList = useMemo(() => {
    const fromApi = (classes || []).map((c) => c.class_id);
    const merged = Array.from(new Set([...CLASS_OPTIONS, ...fromApi]));
    return merged.sort();
  }, [classes]);

  const [picking, setPicking] = useState(false);

  return (
    <Container size="lg" className="py-8 lg:py-12">
      <PageHeader
        title={`Subjects — ${classLabel(classId)}`}
        description={
          status === "anonymous"
            ? "Browse any subject. The first chapter of each subject is free to preview."
            : "Pick a subject to keep going."
        }
        actions={
          <button
            type="button"
            onClick={() => setPicking((p) => !p)}
            className={cn(
              "inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm",
              "hover:border-border-strong hover:bg-surface-2 transition-colors",
            )}
            aria-expanded={picking}
          >
            Change class
            <ChevronRight
              className={cn("h-4 w-4 transition-transform", picking && "rotate-90")}
            />
          </button>
        }
      />

      {picking ? (
        <div className="mb-8 flex flex-wrap gap-2">
          {classList.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                setParams({ class: id });
                setPicking(false);
              }}
              className={cn(
                "rounded-lg border px-4 py-2 text-sm font-medium transition-colors",
                id === classId
                  ? "border-brand bg-brand-soft text-brand"
                  : "border-border hover:border-border-strong hover:bg-surface-2",
              )}
            >
              {classLabel(id)}
            </button>
          ))}
        </div>
      ) : null}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="p-5">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <Skeleton className="mt-3 h-5 w-32" />
              <Skeleton className="mt-2 h-4 w-20" />
            </Card>
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          title="Couldn't load subjects"
          body="Refresh the page or try again in a moment."
        />
      ) : !subjects || subjects.length === 0 ? (
        <EmptyState
          title={`No subjects yet for ${classLabel(classId)}`}
          body="We're adding more classes. Check back soon or try a different class."
        />
      ) : (
        <StaggerList className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {subjects.map((s) => {
            const Icon = getSubjectIcon(s.subject_name);
            return (
              <StaggerItem key={s.subject_id}>
              <Link
                to={`/subjects/${encodeURIComponent(s.subject_id)}/chapters`}
                className={cn(
                  "block group rounded-xl border border-border bg-surface dark:bg-surface p-5 h-full",
                  "hover:border-border-strong hover:-translate-y-px hover:shadow-[0_4px_12px_rgb(15_23_42_/_0.06)] dark:hover:bg-surface-2 dark:hover:shadow-none",
                  "transition-[border-color,box-shadow,transform] duration-200 ease-out",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                )}
              >
                <div className="grid h-11 w-11 place-items-center rounded-lg bg-brand-soft text-brand">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold tracking-tight group-hover:text-brand transition-colors">
                  {s.subject_name}
                </h3>
                <div className="mt-1 flex items-center gap-2 text-sm text-text-muted">
                  <span>{s.chapter_count ?? 0} chapters</span>
                  {status === "anonymous" ? (
                    <Badge tone="neutral" className="ml-auto">
                      Demo available
                    </Badge>
                  ) : null}
                </div>
              </Link>
              </StaggerItem>
            );
          })}
        </StaggerList>
      )}
    </Container>
  );
}
